import { SegmentedControl, Select } from '@mantine/core';
import {
  IconAlertTriangle,
  IconCheck,
  IconClock,
  IconRosetteDiscountCheck,
  IconTool,
} from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '../../PageHeader.js';
import { Panel } from '../../Panel.js';
import { StatTile } from '../../StatTile.js';
import { errorCode } from '../personnel/source.js';
import { usePmsQuery } from '../pms/PmsWorkParts.js';
import lib from '../pms-library/PmsLibrary.module.css';
import { LoadError, TableSkeleton } from '../purchasing/table.js';
import classes from './Drydock.module.css';
import { DrydockReportButtons } from './ReportButtons.js';
import { useDrydockReportExport } from './report-export.js';
import { JobTable, type ScopeOpen, ScopeOverlays } from './ScopePanels.js';
import type { DrydockScopeSource } from './source.js';
import type { DrydockJobList, DrydockJobListRow, DrydockScope } from './types.js';

export interface DrydockJobListScreenProps {
  source: DrydockScopeSource;
}

type Loaded<T> = { kind: 'loading' } | { kind: 'error'; code: string } | { kind: 'ready'; data: T };

export const JOB_LIST_FILTERS = ['yard', 'awaiting', 'verification', 'pending', 'all'] as const;
export type JobListFilter = (typeof JOB_LIST_FILTERS)[number];

const ALL = 'all';

/**
 * Süzgeç: Yard Job List onaylı (ve hash'i tutan) işler ile onaysız başlatılan ek işler ("Awaiting
 * office"); ofiste bekleyenler; tamamlanıp ofis doğrulaması bekleyenler (Faz 5); taşınmamış
 * Pending işler (Faz 4, ADR 0129 #1968); hepsi.
 */
export function filterJobs(
  rows: readonly DrydockJobListRow[],
  filter: JobListFilter,
  projectId: string | null,
): DrydockJobListRow[] {
  const matches = (j: DrydockJobListRow) => {
    switch (filter) {
      case 'all':
        return true;
      case 'yard':
        return j.status === 'approved' || j.execution?.awaitingOffice === true;
      case 'awaiting':
        return j.status === 'for_review';
      case 'verification':
        return j.execution?.state === 'completed';
      case 'pending':
        return j.execution?.state === 'pending' && !j.carriedTo;
    }
  };
  return rows.filter((j) => (projectId === null || j.projectId === projectId) && matches(j));
}

/**
 * Maintenance (PMS) ▸ Dry Docking ▸ Yard Job List (ADR 0129 Ek Faz 3; Excel Screen 8, 11): ofisin
 * onayladığı işler tersane iş listesine düşer; ofiste bekleyen işler Superintendent'ın kuyruğudur
 * (Approve / Return / Reject). Gemide salt liste ve iş çekmecesi; içerik değişen iş yeniden onaya
 * düşer ve listeden çıkar.
 */
export function DrydockJobListScreen({ source }: DrydockJobListScreenProps) {
  const { t } = useTranslation();
  const [query, setQuery] = usePmsQuery();
  const office = source.scope === 'office';
  const vesselId = office ? query.get('vessel') : null;
  const projectId = query.get('project');
  const filter = (JOB_LIST_FILTERS as readonly string[]).includes(query.get('filter') ?? '')
    ? (query.get('filter') as JobListFilter)
    : office
      ? 'awaiting'
      : 'yard';
  const [list, setList] = useState<Loaded<DrydockJobList>>({ kind: 'loading' });
  const [scope, setScope] = useState<DrydockScope | null>(null);
  const [open, setOpen] = useState<ScopeOpen | null>(null);
  const [error, setError] = useState<string | null>(null);
  const reports = useDrydockReportExport(source);
  // Derin bağlantı (`?job=`): filo görünümünden ve bildirimlerden işin çekmecesi açılır
  const jobParam = query.get('job');
  const linked = useRef<string | null>(null);

  const load = useCallback(async () => {
    try {
      setList({ kind: 'ready', data: await source.jobs({ vesselId }) });
    } catch (err) {
      setList({ kind: 'error', code: errorCode(err) });
    }
  }, [source, vesselId]);
  useEffect(() => {
    void load();
  }, [load]);

  const rows = list.kind === 'ready' ? list.data.jobs : [];
  const projects = useMemo(() => {
    const seen = new Map<string, string>();
    for (const j of rows) {
      seen.set(
        j.projectId,
        `${j.projectNo} · ${office && j.vesselName ? `${j.vesselName} · ` : ''}${j.projectTitle}`,
      );
    }
    return [...seen].map(([value, label]) => ({ value, label }));
  }, [rows, office]);
  const shown = filterJobs(rows, filter, projectId);
  const count = (f: JobListFilter) => filterJobs(rows, f, projectId).length;

  const openJob = useCallback(
    async (row: DrydockJobListRow) => {
      setError(null);
      try {
        setScope(await source.load(row.projectId));
        setOpen({ kind: 'job', id: row.id });
      } catch (err) {
        setError(errorCode(err));
      }
    },
    [source],
  );
  useEffect(() => {
    if (list.kind !== 'ready' || !jobParam || linked.current === jobParam) return;
    linked.current = jobParam;
    const row = list.data.jobs.find((j) => j.id === jobParam);
    if (row) void openJob(row);
  }, [list, jobParam, openJob]);

  /** Seçili projenin Yard Job List PDF'i (onaysız iş işaretli; Faz 5). */
  async function jobListReport(mode: 'save' | 'print') {
    if (!projectId) return null;
    try {
      return await reports.jobList(await source.load(projectId), mode);
    } catch (err) {
      return t('drydock.errors.generic', { code: errorCode(err) });
    }
  }

  return (
    <div className={lib.screen}>
      <PageHeader
        title={t('drydock.jobList.title')}
        description={t(
          office ? 'drydock.jobList.officeDescription' : 'drydock.jobList.description',
        )}
        actions={
          projectId ? (
            <DrydockReportButtons
              label={t('drydockReport.actions.jobList')}
              busy={reports.busy}
              run={jobListReport}
            />
          ) : undefined
        }
      />
      {list.kind === 'loading' ? (
        <TableSkeleton columns={8} label={t('drydock.jobList.title')} />
      ) : list.kind === 'error' ? (
        <Panel>
          <LoadError
            code={list.code}
            title={t('drydock.jobList.error')}
            onRetry={() => {
              setList({ kind: 'loading' });
              void load();
            }}
          />
        </Panel>
      ) : (
        <>
          <div className={classes.filters}>
            {office ? (
              <Select
                label={t('drydock.work.vessel')}
                data={[
                  { value: ALL, label: t('drydock.work.allVessels') },
                  ...list.data.vessels.map((v) => ({ value: v.id, label: v.name })),
                ]}
                value={vesselId ?? ALL}
                allowDeselect={false}
                onChange={(value) =>
                  setQuery({ vessel: value === ALL ? null : value, project: null })
                }
              />
            ) : null}
            <Select
              label={t('drydock.jobList.project')}
              data={[{ value: ALL, label: t('drydock.jobList.allProjects') }, ...projects]}
              value={projectId ?? ALL}
              allowDeselect={false}
              searchable
              onChange={(value) => setQuery({ project: value === ALL ? null : value })}
            />
          </div>
          <div className={classes.stats}>
            <StatTile
              icon={<IconCheck size={20} />}
              label={t('drydock.jobList.filters.yard')}
              value={count('yard')}
              tone="success"
              wrapLabel
            />
            <StatTile
              icon={<IconClock size={20} />}
              label={t('drydock.jobList.filters.awaiting')}
              value={count('awaiting')}
              tone={count('awaiting') > 0 ? 'warning' : 'default'}
              wrapLabel
            />
            <StatTile
              icon={<IconRosetteDiscountCheck size={20} />}
              label={t('drydock.jobList.filters.verification')}
              value={count('verification')}
              tone={count('verification') > 0 ? 'warning' : 'default'}
              wrapLabel
            />
            <StatTile
              icon={<IconAlertTriangle size={20} />}
              label={t('drydock.jobList.filters.pending')}
              value={count('pending')}
              tone={count('pending') > 0 ? 'danger' : 'default'}
              wrapLabel
            />
            <StatTile
              icon={<IconTool size={20} />}
              label={t('drydock.jobList.filters.all')}
              value={count('all')}
              wrapLabel
            />
          </div>
          {error ? <p role="alert">{t('drydock.errors.generic', { code: error })}</p> : null}
          <Panel title={t(`drydock.jobList.filters.${filter}`)} flush>
            <div className={lib.toolbar}>
              <SegmentedControl
                aria-label={t('drydock.jobList.filter')}
                data={JOB_LIST_FILTERS.map((value) => ({
                  value,
                  label: t(`drydock.jobList.filters.${value}`),
                }))}
                value={filter}
                onChange={(value) => setQuery({ filter: value })}
              />
            </div>
            <JobTable
              rows={shown}
              project
              onOpen={(row) => void openJob(row as DrydockJobListRow)}
            />
          </Panel>
          {scope ? (
            <ScopeOverlays
              scope={scope}
              source={source}
              open={open}
              onOpen={setOpen}
              onClose={() => {
                setOpen(null);
                if (jobParam) setQuery({ job: null });
              }}
              onChanged={(next) => {
                setScope(next);
                void load();
              }}
            />
          ) : null}
        </>
      )}
    </div>
  );
}
